<template>
    <div>
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">{{ $t('sideMenu.myAccount') }}</h6>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <div class="row">
                <div class="col">
                    <div class="card">
                        <div class="card-body">
                            <el-tabs>
                                <el-tab-pane :label="$t('myAccountPage.personalData')">
                                    <div class="form-short mt-2">
                                        <base-input
                                            :label="$t('forms.name')"
                                            type="text"
                                            name="name"
                                            v-validate="userValidations.name"
                                            :error="getError('name')"
                                            v-model="user.name">
                                        </base-input>

                                        <base-input
                                            :label="$t('forms.emailAddress')"
                                            type="email"
                                            name="email"
                                            v-validate="userValidations.email"
                                            :error="getError('email')"
                                            v-model="user.email">
                                        </base-input>

                                        <base-input
                                            :label="$t('general.phone')"
                                            type="tel"
                                            name="phone"
                                            placeholder="+36301234567"
                                            v-validate="userValidations.phone"
                                            :error="getError('phone')"
                                            v-model="user.phone">
                                        </base-input>

                                        <div class="form-group">
                                            <label class="form-control-label">{{ $t('general.language') }}</label>
                                            <el-select
                                                name="language"
                                                v-validate="userValidations.language"
                                                :error="getError('language')"
                                                v-model="user.language">
                                                <el-option
                                                    v-for="lang in languages"
                                                    :key="lang.id"
                                                    :value="lang.id"
                                                    :label="lang.name"></el-option>
                                            </el-select>
                                        </div>
                                        <div class="form-group align-items-center">
                                            <base-checkbox v-model="user['2fa']">
                                                {{ $t('myAccountPage.twoFactorAuth') }}
                                            </base-checkbox>
                                        </div>
                                        <div class="clearfix"></div>
                                    </div>
                                    <base-button
                                        class="mt-3"
                                        type="success"
                                        @click.prevent="updatePersonalData">{{ $t('general.save') }}
                                    </base-button>
                                </el-tab-pane>

                                <el-tab-pane :label="$t('myAccountPage.editPassword')">
                                    <div class="form-short mt-2">
                                        <base-input
                                            type="password"
                                            :label="$t('forms.oldPassword')"
                                            :placeholder="$t('forms.oldPassword')"
                                            v-model="password.oldPassword">
                                        </base-input>

                                        <base-input
                                            type="password"
                                            name="password"
                                            :label="$t('forms.newPassword')"
                                            :placeholder="$t('forms.newPassword')"
                                            :error="getError('password')"
                                            v-validate="passwordValidations.password"
                                            v-model="password.password">
                                        </base-input>

                                        <base-input
                                            type="password"
                                            name="confirm"
                                            data-vv-as="password"
                                            :label="$t('forms.confirmNewPassword')"
                                            :placeholder="$t('forms.confirmNewPassword')"
                                            :error="getError('confirm')"
                                            v-validate="passwordValidations.confirmPassword"
                                            v-model="password.confirmPassword">
                                        </base-input>

                                        <base-button
                                            class="mt-3"
                                            type="success"
                                            @click.prevent="changePassword">{{ $t('forms.changePassword') }}
                                        </base-button>
                                    </div>
                                </el-tab-pane>

                                <el-tab-pane :label="$t('myAccountPage.security')">
                                    <el-table :data="user.devices" border>
                                        <el-table-column
                                            :label="$t('general.country')"
                                            prop="country"
                                            width="140px"
                                            sortable/>

                                        <el-table-column
                                            :label="$t('general.city')"
                                            prop="city"
                                            sortable/>

                                        <el-table-column
                                            :label="$t('general.operatingSystem')"
                                            prop="os"
                                            sortable/>

                                        <el-table-column
                                            :label="$t('general.browser')"
                                            prop="browser"
                                            sortable/>

                                        <el-table-column
                                            :label="$t('general.ipAddress')"
                                            prop="ip_address"
                                            width="160px"
                                            sortable/>

                                        <el-table-column
                                            :label="$t('general.lastLogin')"
                                            prop="updated_at"
                                            width="180px"
                                            sortable>
                                            <template v-slot="{row: device}">
                                                {{ $moment(device.updated_at).format('YYYY-MM-DD HH:mm:ss') }}
                                            </template>
                                        </el-table-column>

                                        <el-table-column :label="$t('general.actions')" width="180px" sortable>
                                            <template v-slot="{row: device}">
                                                <base-button
                                                    v-if="deviceToken !== device.token"
                                                    type="danger"
                                                    size="sm"
                                                    @click="removeDeviceAttention(device.id)">{{ $t('general.delete') }}
                                                </base-button>
                                                <span
                                                    v-else
                                                    class="text-danger">{{ $t('myAccountPage.currentDevice') }}</span>
                                            </template>
                                        </el-table-column>
                                    </el-table>
                                </el-tab-pane>
                            </el-tabs>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <el-dialog
            :title="$t('general.warning')"
            :visible.sync="showRemoveDeviceAttentionModal"
            width="550px"
            center>
            <div class="h2 text-center">{{ $t('myAccountPage.warningQuestion') }}</div>
            <div slot="footer" class="dialog-footer">
                <base-button
                    outline
                    type="secondary"
                    @click="showRemoveDeviceAttentionModal = false">{{ $t('general.cancel') }}
                </base-button>
                <base-button type="danger" @click="removeDevice()">{{ $t('general.delete') }}</base-button>
            </div>
        </el-dialog>

        <el-dialog
            :visible.sync="showEmailChanged"
            width="550px"
            center>
            <div class="h3 text-center">{{ $t('myAccountPage.checkEmailMessage') }}</div>
            <div slot="footer" class="dialog-footer">
                <base-button
                    type="success"
                    @click="showEmailChanged = false">{{ $t('general.close') }}
                </base-button>
            </div>
        </el-dialog>
    </div>
</template>
<script>
    import {Dialog, Option, Select, Table, TableColumn, TabPane, Tabs} from 'element-ui'
    import VeeValidateMixin from '@/mixins/vee-validate-mixin'

    export default {
        mixins: [VeeValidateMixin],
        components: {
            [Table.name]: Table,
            [TableColumn.name]: TableColumn,
            [Tabs.name]: Tabs,
            [TabPane.name]: TabPane,
            [Select.name]: Select,
            [Option.name]: Option,
            [Dialog.name]: Dialog,
        },
        data() {
            return {
                user: {},
                languages: [],
                userValidations: {
                    name: {
                        required: true,
                        min: 5,
                        max: 191,
                    },
                    email: {
                        required: true,
                        email: true,
                        max: 191,
                    },
                    phone: {
                        required: false,
                        max: 30,
                        regex: /^\+[0-9\s\-()]{7,20}$/,
                    },
                    language: {
                        required: true,
                        integer: true,
                    },
                },
                password: {},
                passwordValidations: {
                    password: {
                        min: 5,
                    },
                    confirmPassword: {
                        confirmed: 'password',
                    },
                },
                deviceToken: this.$cookies.get('device_token'),
                showRemoveDeviceAttentionModal: false,
                removeDeviceId: null,
                showEmailChanged: false,
            }
        },
        methods: {
            async updatePersonalData() {
                const isValid = await this.$validator.validate('userValidations.*');

                if (isValid) {
                    this.$axios.put('/api/settings/updatePersonalData', {
                        name: this.user.name,
                        email: this.user.email,
                        phone: this.user.phone,
                        language: this.user.language,
                        "2fa": this.user["2fa"],
                    })
                        .then(res => {
                            if (res.data.emailChanged) {
                                this.showEmailChanged = true;
                                delete res.data.emailChanged;
                            } else {
                                this.$notify({
                                    title: this.$t('myAccountPage.userUpdated'),
                                });
                            }

                            this.$store.commit('setUser', res.data)
                        })
                        .catch(({response}) => {
                            this.$notify({
                                title: response.data.message,
                                type: 'danger',
                            });
                        })
                }
            },
            async changePassword() {
                const isValid = await this.$validator.validate('passwordValidations.*');

                if (isValid) {
                    this.$axios.put('/api/settings/changePassword', this.password)
                        .then(() => {
                            this.$notify({
                                title: this.$t('myAccountPage.passwordChanged'),
                            })
                        })
                        .catch(({response}) => {
                            this.$notify({
                                title: response.data.message,
                                type: 'danger',
                            });
                        })
                }
            },
            removeDeviceAttention(id) {
                this.showRemoveDeviceAttentionModal = true;
                this.removeDeviceId = id;
            },
            async removeDevice() {
                this.$axios.delete('/api/devices/' + this.removeDeviceId)
                    .then(() => {
                        this.$notify({
                            title: this.$t('myAccountPage.deviceDeleted'),
                        });
                        const deviceIndex = this.user.devices.findIndex(d => d.id === this.removeDeviceId);
                        this.user.devices.splice(deviceIndex, 1);
                        this.user = {...this.user};
                    })
                    .catch(({response}) => {
                        this.$notify({
                            title: response.data.message,
                            type: 'danger',
                        });
                    })
                    .then(() => {
                        this.showRemoveDeviceAttentionModal = false;
                        this.removeDeviceId = null;
                    });
            }
        },
        async created() {
            this.languages = (await this.$axios.get('/api/languages')).data;
            this.user = (await this.$axios.get('/api/profile')).data.data;
        }
    }
</script>

<style lang="scss">
    .form-short {
        max-width: 400px;
    }
</style>
